// Combine View component
// LEGO builder for mixing patterns from different categories

import { patternsApi, type Pattern, type PatternType, type PatternStatus } from '../services/patterns-api';

interface SelectedPatterns {
  hook: Pattern | null;
  script: Pattern | null;
  visual: Pattern | null;
  cta: Pattern | null;
  concept: Pattern | null;
}

export class CombineView {
  private patternsByType: Record<PatternType, Pattern[]> = {
    hook: [],
    script: [],
    visual: [],
    cta: [],
    concept: []
  };

  private selected: SelectedPatterns = {
    hook: null,
    script: null,
    visual: null,
    cta: null,
    concept: null
  };

  private isLoading = false;

  // Type colors
  private typeColors: Record<PatternType, string> = {
    hook: '#49e619',
    script: '#60a5fa',
    visual: '#a78bfa',
    cta: '#fbbf24',
    concept: '#f472b6'
  };

  constructor() {
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Save button
    document.getElementById('combine-btn-save')?.addEventListener('click', () => {
      this.saveCombination();
    });

    // Send to GetPoppy button
    document.getElementById('combine-btn-send')?.addEventListener('click', () => {
      this.sendToGetPoppy();
    });
  }

  async loadPatterns(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      // Load patterns for each type
      const types: PatternType[] = ['hook', 'script', 'visual', 'cta', 'concept'];

      await Promise.all(types.map(async (type) => {
        try {
          const result = await patternsApi.getByType(type, undefined, 20);
          this.patternsByType[type] = result.patterns;
        } catch (e) {
          console.error(`Failed to load ${type} patterns:`, e);
          this.patternsByType[type] = [];
        }
      }));

      this.renderColumns();
    } catch (error) {
      console.error('Failed to load patterns for combine:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private renderColumns(): void {
    const types: PatternType[] = ['hook', 'script', 'visual', 'cta', 'concept'];

    types.forEach(type => {
      const patterns = this.patternsByType[type];
      const container = document.querySelector(`.combine-column-items[data-type="${type}"]`);
      const countEl = document.querySelector(`.combine-column[data-type="${type}"] .combine-count`);
      const showMoreBtn = document.querySelector(`.combine-show-more[data-type="${type}"]`) as HTMLElement;

      if (countEl) {
        countEl.textContent = `(${patterns.length})`;
      }

      if (!container) return;

      // Render items (show first 10)
      const visible = patterns.slice(0, 10);
      container.innerHTML = visible.map(p => this.createPatternItem(p, type)).join('');

      // Show "show more" if there are more
      if (patterns.length > 10 && showMoreBtn) {
        showMoreBtn.classList.remove('hidden');
        showMoreBtn.textContent = `Show ${patterns.length - 10} more`;
        showMoreBtn.onclick = () => this.showMorePatterns(type);
      }

      // Add click listeners
      container.querySelectorAll('.combine-item').forEach(item => {
        item.addEventListener('click', () => {
          const id = (item as HTMLElement).dataset.id;
          const pattern = patterns.find(p => p.id === id);
          if (pattern) {
            this.selectPattern(type, pattern);
          }
        });
      });
    });
  }

  private createPatternItem(pattern: Pattern, type: PatternType): string {
    const isSelected = this.selected[type]?.id === pattern.id;
    const statusLabel = this.getStatusLabel(pattern.status);

    return `
      <div class="combine-item ${isSelected ? 'selected' : ''}" data-id="${pattern.id}" data-type="${type}">
        <div class="combine-item-info">
          <span class="combine-item-name">${this.escapeHtml(pattern.name)}</span>
          <div class="combine-item-meta">
            <span class="combine-item-dot ${pattern.status}"></span>
            <span class="combine-item-status">${statusLabel}</span>
          </div>
        </div>
        <div class="combine-item-radio">
          ${isSelected ? '<span class="material-symbols-outlined">check</span>' : ''}
        </div>
      </div>
    `;
  }

  private showMorePatterns(type: PatternType): void {
    const patterns = this.patternsByType[type];
    const container = document.querySelector(`.combine-column-items[data-type="${type}"]`);
    const showMoreBtn = document.querySelector(`.combine-show-more[data-type="${type}"]`) as HTMLElement;

    if (!container) return;

    // Render all items
    container.innerHTML = patterns.map(p => this.createPatternItem(p, type)).join('');

    // Hide show more button
    if (showMoreBtn) {
      showMoreBtn.classList.add('hidden');
    }

    // Re-add click listeners
    container.querySelectorAll('.combine-item').forEach(item => {
      item.addEventListener('click', () => {
        const id = (item as HTMLElement).dataset.id;
        const pattern = patterns.find(p => p.id === id);
        if (pattern) {
          this.selectPattern(type, pattern);
        }
      });
    });
  }

  private selectPattern(type: PatternType, pattern: Pattern): void {
    // Toggle selection
    if (this.selected[type]?.id === pattern.id) {
      this.selected[type] = null;
    } else {
      this.selected[type] = pattern;
    }

    // Update column UI
    const container = document.querySelector(`.combine-column-items[data-type="${type}"]`);
    if (container) {
      container.querySelectorAll('.combine-item').forEach(item => {
        const id = (item as HTMLElement).dataset.id;
        const isSelected = this.selected[type]?.id === id;
        item.classList.toggle('selected', isSelected);

        const radio = item.querySelector('.combine-item-radio');
        if (radio) {
          radio.innerHTML = isSelected ? '<span class="material-symbols-outlined">check</span>' : '';
        }
      });
    }

    // Update preview panel
    this.renderPreview();
  }

  private renderPreview(): void {
    const selectedList = document.getElementById('combine-selected-list');
    const emptyEl = document.getElementById('combine-empty');
    const scoreSection = document.getElementById('combine-score-section');
    const saveBtn = document.getElementById('combine-btn-save') as HTMLButtonElement;
    const sendBtn = document.getElementById('combine-btn-send') as HTMLButtonElement;

    // Get selected items
    const selectedItems = Object.entries(this.selected)
      .filter(([_, pattern]) => pattern !== null)
      .map(([type, pattern]) => ({ type: type as PatternType, pattern: pattern! }));

    if (selectedItems.length === 0) {
      // Show empty state
      if (emptyEl) emptyEl.classList.remove('hidden');
      if (scoreSection) scoreSection.classList.add('hidden');
      if (saveBtn) saveBtn.disabled = true;
      if (sendBtn) sendBtn.disabled = true;
      return;
    }

    // Hide empty state
    if (emptyEl) emptyEl.classList.add('hidden');

    // Render selected items
    if (selectedList) {
      selectedList.innerHTML = selectedItems.map(({ type, pattern }) => `
        <div class="combine-selected-item" data-type="${type}">
          <div class="combine-selected-info">
            <span class="combine-selected-bar" data-type="${type}"></span>
            <div class="combine-selected-text">
              <span class="combine-selected-type">${type}</span>
              <span class="combine-selected-name">${this.escapeHtml(pattern.name)}</span>
            </div>
          </div>
          <button class="combine-selected-remove" data-type="${type}" title="Remove">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
      `).join('');

      // Add remove listeners
      selectedList.querySelectorAll('.combine-selected-remove').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const type = (btn as HTMLElement).dataset.type as PatternType;
          this.selected[type] = null;
          this.renderColumns(); // Re-render to update selection state
          this.renderPreview();
        });
      });
    }

    // Update score
    this.updateCompatibilityScore(selectedItems.length);

    // Enable buttons
    if (saveBtn) saveBtn.disabled = selectedItems.length < 2;
    if (sendBtn) sendBtn.disabled = selectedItems.length < 2;
  }

  private updateCompatibilityScore(count: number): void {
    const scoreSection = document.getElementById('combine-score-section');
    const scorePath = document.getElementById('combine-score-path');
    const scoreValue = document.getElementById('combine-score-value');
    const scoreTitle = document.getElementById('combine-score-title');
    const scoreDesc = document.getElementById('combine-score-desc');

    if (count < 2) {
      if (scoreSection) scoreSection.classList.add('hidden');
      return;
    }

    if (scoreSection) scoreSection.classList.remove('hidden');

    // Calculate mock compatibility score (in real app, would be API-based)
    // Higher score for more selections, with some randomness
    const baseScore = Math.min(count * 20, 70);
    const randomBonus = Math.floor(Math.random() * 25);
    const score = Math.min(baseScore + randomBonus, 98);

    // Update UI
    if (scorePath) {
      scorePath.setAttribute('stroke-dasharray', `${score}, 100`);
    }
    if (scoreValue) {
      scoreValue.textContent = `${score}%`;
    }
    if (scoreTitle) {
      if (score >= 80) {
        scoreTitle.textContent = 'Strong Compatibility';
      } else if (score >= 60) {
        scoreTitle.textContent = 'Good Compatibility';
      } else {
        scoreTitle.textContent = 'Moderate Compatibility';
      }
    }
    if (scoreDesc) {
      scoreDesc.textContent = `This combination has a ${score >= 80 ? 'high' : score >= 60 ? 'good' : 'moderate'} probability of success based on current trends.`;
    }
  }

  private async saveCombination(): Promise<void> {
    const selected = this.getSelectedPatterns();
    if (selected.length < 2) return;

    // Record each pattern as "used"
    for (const { type, pattern } of selected) {
      try {
        await patternsApi.recordSelection({
          patternId: pattern.id,
          patternType: type,
          action: 'used',
          notes: `Combined with: ${selected.filter(s => s.type !== type).map(s => s.pattern.name).join(', ')}`
        });
      } catch (e) {
        console.error('Failed to record selection:', e);
      }
    }

    // Show success feedback
    alert(`Combination saved!\n\nPatterns:\n${selected.map(s => `• ${s.type}: ${s.pattern.name}`).join('\n')}`);
  }

  private sendToGetPoppy(): void {
    const selected = this.getSelectedPatterns();
    if (selected.length < 2) return;

    // Build a summary for GetPoppy (in real app, would open integration)
    const summary = selected.map(s => `${s.type.toUpperCase()}: ${s.pattern.name}`).join('\n');

    // For now, show what would be sent
    alert(`Ready to send to GetPoppy!\n\n${summary}\n\n(Integration coming soon)`);

    // TODO: Integrate with GetPoppy API
    // window.open(`https://getpoppy.ai/create?patterns=${encodeURIComponent(JSON.stringify(selected))}`, '_blank');
  }

  private getSelectedPatterns(): Array<{ type: PatternType; pattern: Pattern }> {
    return Object.entries(this.selected)
      .filter(([_, pattern]) => pattern !== null)
      .map(([type, pattern]) => ({ type: type as PatternType, pattern: pattern! }));
  }

  private getStatusLabel(status: PatternStatus): string {
    const labels: Record<PatternStatus, string> = {
      emerging: 'Trending',
      active: 'Stable',
      saturated: 'Popular',
      declining: 'Fading'
    };
    return labels[status] || status;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
